import { lazy, useEffect, useRef } from 'react';

import type { SlideProps } from '@mui/material';
import { IconButton, Slide } from '@mui/material';
import type { SnackbarKey } from 'notistack';
import { SnackbarProvider } from 'notistack';
import { Route, Routes, useLocation } from 'react-router-dom';

import { ReactComponent as IconClose } from './assets/images/ui/icon-close.svg';

const NotFound = lazy(() => import('./views/NotFound'));
const Redirect = lazy(() => import('./views/Redirect'));
const InvoicePay = lazy(() => import('./views/InvoicePay'));
const RecurringPay = lazy(() => import('./views/RecurringPay'));
const WalletPay = lazy(() => import('./views/WalletPay'));

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createWeb3Modal } from '@web3modal/wagmi/react';
import { defaultWagmiConfig } from '@web3modal/wagmi/react/config';
import { CollectUserDataProvider } from 'components/CollectUserDataProvider/CollectUserDataProvider';
import CookieModal from 'components/CookieModal/CookieModal';
import { SupportButton } from 'components/SupportButton/SupportButton';
import Cookies from 'js-cookie';
import { useTranslation } from 'react-i18next';
import { getLocaleLink } from 'utils/helpers/getLocaleLink';
import { MercuryoRedirect } from 'views/InvoicePay/MercuryoRedirect/MercuryoRedirect';
import { Widget } from 'views/Widget/Widget';
import { WidgetCreateInvoice } from 'views/WidgetCreateInvoice/WidgetCreateInvoice';
import { WagmiProvider } from 'wagmi';
import { arbitrum, avalanche, bsc, mainnet, polygon } from 'wagmi/chains';

// Wagmi client
const chains = [polygon, bsc, mainnet, arbitrum, avalanche] as const;
const projectId = 'f59235ede3d39af9df74a3bd9beb4f8b'; //dc5b98abd3c8c340ac7da2b1e035d892

const queryClient = new QueryClient();

const metadata = {
  description: 'Web3Modal Example',
  icons: ['https://avatars.githubusercontent.com/u/37784886'],
  name: 'Web3Modal',
  url: 'https://web3modal.com',
};

const wagmiConfig = defaultWagmiConfig({
  chains,
  metadata,
  projectId,
});

createWeb3Modal({ projectId, wagmiConfig });

const TransitionLeft = (props: SlideProps) => <Slide {...props} direction='left' />;

function App() {
  const notistackRef = useRef<SnackbarProvider>(null);

  const onClickDismiss = (key: SnackbarKey) => () => {
    notistackRef.current?.closeSnackbar(key);
  };

  // RTL TRANSLATION SUPPORT
  const { i18n } = useTranslation();

  document.body.dir = i18n?.dir();

  const { search } = useLocation();

  useEffect(() => {
    const utmTags: string[] = [
      'utm_source',
      'utm_content',
      'utm_medium',
      'utm_campaign',
      'utm_term',
      'click_id',
      'cpa',
      'user_id',
      'client_id',
    ];

    if (search?.length) {
      const queryParams = search.replace(/^\?/, '').split('&'); // ['utm=value'];

      const queryObject: { [key: string]: string } = {}; // {utm: value}

      queryParams.forEach((item) => {
        const equalSymbolIndex = item.indexOf('=');

        const key = item.slice(0, equalSymbolIndex);
        const value = item.slice(equalSymbolIndex + 1);

        queryObject[key] = value;
      });

      utmTags.map((item: string) => {
        if (queryObject[item]) {
          Cookies.set(item, queryObject[item], {
            domain: 'cryptomus.com',
            expires: 7,
          });
        } else {
          if (Cookies.get(item) === 'undefined') {
            Cookies.remove(item, { domain: 'cryptomus.com' });
          }
        }

        return;
      });
    }
  }, [search]);

  // userInfo
  // useQuery("getUserInfo", () => UserService.getUserInfo());

  // useQuery("getUserWallets", () => UserService.getUserWallets());

  return (
    <div className='App'>
      <WagmiProvider config={wagmiConfig}>
        <QueryClientProvider client={queryClient}>
          <SnackbarProvider
            ref={notistackRef}
            maxSnack={3}
            TransitionComponent={TransitionLeft}
            action={(key) => (
              <IconButton className='snack-close' onClick={onClickDismiss(key)}>
                <IconClose />
              </IconButton>
            )}
            anchorOrigin={{
              horizontal: 'right',
              vertical: 'top',
            }}
          >
            <CollectUserDataProvider>
              <Routes>
                <Route element={<InvoicePay />} path='pay/:id' />
                <Route element={<WalletPay />} path='wallet/:id' />
                <Route element={<RecurringPay />} path='recurring/:id' />
                <Route element={<Widget />} path='widget/:id' />
                <Route element={<MercuryoRedirect />} path='mercuryo-status/:id' />
                <Route element={<WidgetCreateInvoice />} path='widget/create-invoice/:id' />

                <Route
                  element={<Redirect url={getLocaleLink('https://cryptomus.com/gateway', i18n.language)} />}
                  path='pay'
                />
                <Route element={<NotFound />} path='*' />
              </Routes>

              <CookieModal />

              <SupportButton />
            </CollectUserDataProvider>
          </SnackbarProvider>
        </QueryClientProvider>
      </WagmiProvider>
    </div>
  );
}

export default App;
